import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { api, type Me, type ServiceKey, type ServiceKeyRequest } from "../api";
import { teamLabel } from "../teamFilter";
import { CopyButton, Dialog, Empty, ErrorMessage, Loading, TagChips } from "../components";
import { DivisionPicker } from "../DivisionPicker";
import { money, when } from "../format";
import { useForgeEvent } from "../hooks";
import { RESET_LABEL } from "../KeyForm";
import { ownerOptions } from "../personFilter";
import { pick } from "../search";
import { SearchPicker } from "../SearchPicker";
import { shortAlias } from "./Keys";

/**
 * Keys for shared applications (TAI-2298), administrators only. Each is made in the service
 * organization with no user: its spend lands on the key, its division and business-unit tags and
 * the organization, never on a person. A named owner answers for it. Requests and approvals for
 * non-administrators come later (plan §2 item 5).
 *
 * Every key needs an owner and a different backup owner (key policy §4.6, TAI-2567). Keys made in
 * the LiteLLM portal have neither, so they are flagged, and saving an edit requires both.
 */
const missingOwners = (k: ServiceKey) => !k.owner || !k.backupOwner;

export function ServiceKeysPage({ me }: { me: Me }) {
  const q = useQuery({ queryKey: ["service-keys"], queryFn: api.adminServiceKeys });
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ServiceKey | null>(null);
  const [rotating, setRotating] = useState<ServiceKey | null>(null);
  const [revoking, setRevoking] = useState<ServiceKey | null>(null);
  const [created, setCreated] = useState<{ key: string; summary: ServiceKey } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [missingOnly, setMissingOnly] = useState(false);
  const rotate = useMutation({
    mutationFn: (id: string) => api.adminRotateServiceKey(id),
    onSuccess: async (k) => { setRotating(null); setNotice(null); await qc.invalidateQueries({ queryKey: ["service-keys"] }); setCreated(k); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.adminRevokeServiceKey(id),
    onSuccess: async () => {
      setRevoking(null);
      setNotice("Service key revoked. It can take up to five minutes to stop working everywhere.");
      await qc.invalidateQueries({ queryKey: ["service-keys"] });
    },
  });
  // The confirmations keep showing the key's name while they animate closed.
  const lastRotating = useRef<ServiceKey | null>(null);
  const lastRevoking = useRef<ServiceKey | null>(null);
  if (rotating) lastRotating.current = rotating;
  if (revoking) lastRevoking.current = revoking;
  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data.configured) {
    return (
      <forge-card>
        <Empty title="Service keys are not configured" message="The service organization is not set for this app (KEYPORTAL_SERVICE_ORG_ID)." />
      </forge-card>
    );
  }
  const missing = q.data.keys.filter(missingOwners).length;
  const keys = missingOnly && missing ? q.data.keys.filter(missingOwners) : q.data.keys;
  return (
    <>
      <p className="forge-typography--body2 muted">
        Keys for shared applications and pipelines, in the internal applications organization. Their spend is
        reported under their division and business unit and never counts against a person&apos;s budget. Each names the
        person who answers for it.
      </p>
      {notice && (
        <forge-inline-message theme="success">
          <forge-icon slot="icon" name="check_circle" />
          <div slot="title">{notice}</div>
        </forge-inline-message>
      )}
      <forge-card>
        <div className="row spread mb-medium">
          <div className="row gap-medium wrap">
            <span className="muted">{q.data.keys.length} service {q.data.keys.length === 1 ? "key" : "keys"}{missing ? `, ${missing} missing an owner or backup owner` : ""}</span>
            {missing > 0 && (
              <label className="row gap-xsmall">
                <forge-checkbox checked={missingOnly || undefined} onClick={() => setMissingOnly((v) => !v)} />
                Show only those
              </label>
            )}
          </div>
          <forge-button variant="raised" disabled={me.readOnly || undefined} onClick={() => setCreating(true)}>
            <forge-icon slot="start" name="add" /> Create a service key
          </forge-button>
        </div>
        {keys.length === 0 ? (
          <Empty title="No service keys yet" message="Create one for an application that needs its own key." />
        ) : (
          <div className="table-wrap">
            <table className="forge-data-table">
              <thead><tr><th>Key</th><th>Owners</th><th>Team</th><th>Labels</th><th>Models</th><th>Budget</th><th>Last used</th><th className="num">Spend (est.)</th><th aria-label="Actions" className="cell-sticky-end" /></tr></thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td><div>{shortAlias(k)}</div><div className="mono muted">…{k.id.slice(-8)}</div></td>
                    <td className="wrap-ok">
                      <div>{k.owner ? <BreakAfterAt text={k.owner} /> : <forge-badge theme="warning">No owner</forge-badge>}</div>
                      <div className="muted">{k.backupOwner ? <>Backup: <BreakAfterAt text={k.backupOwner} /></> : <forge-badge theme="warning">No backup owner</forge-badge>}</div>
                    </td>
                    <td className="wrap-ok">{teamLabel(k) ?? <span className="muted">No team</span>}</td>
                    <td><TagChips division={k.division} businessUnit={k.businessUnit} /></td>
                    <td className="wrap-ok cell-models" title={k.models.join(", ")}>{k.models.length ? (k.models.length > 2 ? `${k.models.slice(0, 2).join(", ")} +${k.models.length - 2}` : k.models.join(", ")) : <span className="muted">all</span>}</td>
                    {/* The reset on its own line keeps the table inside its card at laptop widths. */}
                    <td className="nowrap">{k.maxBudget === null ? <span className="muted">none</span> : <>
                      <div>{money(k.maxBudget)}</div>
                      {k.budgetDuration && <div className="muted">{RESET_LABEL[k.budgetDuration] ?? k.budgetDuration}</div>}
                    </>}</td>
                    <td className="nowrap">{when(k.lastActive)}</td>
                    <td className="num">{money(k.spend)}</td>
                    <td className="nowrap cell-action cell-sticky-end">
                      <forge-icon-button aria-label={`Edit ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setEditing(k)}>
                        <forge-icon name="settings" />
                      </forge-icon-button>
                      <forge-tooltip>Owners, team, labels, models and budget</forge-tooltip>
                      <forge-icon-button aria-label={`Rotate ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setRotating(k)}>
                        <forge-icon name="autorenew" />
                      </forge-icon-button>
                      <forge-tooltip>Replace with a new key</forge-tooltip>
                      <forge-icon-button aria-label={`Revoke ${shortAlias(k)}`} disabled={me.readOnly || undefined} onClick={() => setRevoking(k)}>
                        <forge-icon name="delete_outline" />
                      </forge-icon-button>
                      <forge-tooltip>Revoke</forge-tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </forge-card>
      {/* Mounted only while open, so the form's Forge listeners attach to elements that exist. */}
      {creating && <ServiceKeyDialog me={me} onClose={() => setCreating(false)} onCreated={(k) => { setCreating(false); setNotice(null); setCreated(k); }} />}
      {editing && <ServiceKeyDialog key={editing.id} me={me} target={editing} onClose={() => setEditing(null)} onCreated={() => {}} />}
      <Dialog open={!!rotating} onClose={() => setRotating(null)} label="Rotate service key">
        <h2 className="forge-typography--heading3 card-title">Rotate “{lastRotating.current && shortAlias(lastRotating.current)}”?</h2>
        <p className="forge-typography--body2">
          A new key replaces it, with the same owners, team, labels, models and budget. The current key stops working at once, so the
          application using it fails until the new key is installed.
        </p>
        {rotate.error && <ErrorMessage error={rotate.error} />}
        <div className="row gap-small">
          <forge-button variant="raised" disabled={rotate.isPending || undefined} onClick={() => rotating && rotate.mutate(rotating.id)}>Rotate</forge-button>
          <forge-button onClick={() => setRotating(null)}>Cancel</forge-button>
        </div>
      </Dialog>
      <Dialog open={!!revoking} onClose={() => setRevoking(null)} label="Revoke service key">
        <h2 className="forge-typography--heading3 card-title">Revoke “{lastRevoking.current && shortAlias(lastRevoking.current)}”?</h2>
        <p className="forge-typography--body2">
          The application using it stops working within about five minutes. This cannot be undone; you can create a new key.
        </p>
        {revoke.error && <ErrorMessage error={revoke.error} />}
        <div className="row gap-small">
          <forge-button variant="raised" theme="error" disabled={revoke.isPending || undefined}
            onClick={() => revoking && revoke.mutate(revoking.id)}>Revoke</forge-button>
          <forge-button onClick={() => setRevoking(null)}>Cancel</forge-button>
        </div>
      </Dialog>
      <Dialog open={!!created} onClose={() => setCreated(null)} label="Your new service key" persistent>
        <h2 className="forge-typography--heading3 card-title">Copy the key now</h2>
        <forge-inline-message theme="warning">
          <forge-icon slot="icon" name="warning" />
          <div slot="title">This is the only time it is shown</div>
          <span>Give it to {created?.summary.owner ?? "its owner"} through a secret store, never by chat or email.</span>
        </forge-inline-message>
        {created && (
          <div className="secret-box">
            <code className="mono grow">{created.key}</code>
            <CopyButton text={created.key} />
          </div>
        )}
        <div><forge-button variant="raised" onClick={() => setCreated(null)}>Done</forge-button></div>
      </Dialog>
    </>
  );
}

/** An email that may wrap only after its "@", never inside a name. */
function BreakAfterAt({ text }: { text: string }) {
  const i = text.indexOf("@");
  return i < 0 ? <>{text}</> : <>{text.slice(0, i + 1)}<wbr />{text.slice(i + 1)}</>;
}

/** Create a service key, or with `target` edit one: everything but the name, which is its alias. */
function ServiceKeyDialog({ me, target, onClose, onCreated }: {
  me: Me; target?: ServiceKey; onClose: () => void; onCreated: (k: { key: string; summary: ServiceKey }) => void;
}) {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["admin-users"], queryFn: api.adminUsers });
  const models = useQuery({ queryKey: ["models"], queryFn: api.models });
  const teams = useQuery({ queryKey: ["admin-teams"], queryFn: api.adminTeams });
  const [teamId, setTeamId] = useState(target?.teamId ?? "");
  const [f, setF] = useState<ServiceKeyRequest>(target
    ? { name: shortAlias(target), division: target.division ?? "", businessUnit: target.businessUnit ?? "", owner: target.owner ?? "",
      backupOwner: target.backupOwner ?? "", models: target.models, maxBudget: target.maxBudget, budgetDuration: (target.budgetDuration ?? null) as ServiceKeyRequest["budgetDuration"] }
    : { name: "", division: "", businessUnit: "", owner: "", backupOwner: "", models: [], maxBudget: null, budgetDuration: null });
  const [budgetText, setBudgetText] = useState(target?.maxBudget != null ? String(target.maxBudget) : "");
  const latest = useRef(f);
  latest.current = f;
  // The backup can never be the owner; a recorded owner stays listed even if no longer a user.
  const ownerFilter = useMemo(() => {
    const options = ownerOptions(users.data ?? [], "", target?.owner ?? "");
    return (text: string, value: string | null) => pick(options, text, value);
  }, [users.data, target?.owner]);
  const backupFilter = useMemo(() => {
    const options = ownerOptions(users.data ?? [], f.owner, target?.backupOwner ?? "");
    return (text: string, value: string | null) => pick(options, text, value);
  }, [users.data, f.owner, target?.backupOwner]);
  const modelSel = useRef<HTMLElement>(null);
  const resetSel = useRef<HTMLElement>(null);
  const teamSel = useRef<HTMLElement>(null);
  useForgeEvent(teamSel, "change", (e) => setTeamId(String((e.target as HTMLInputElement).value ?? "")));
  useForgeEvent(modelSel, "change", (e) => {
    const v = (e.target as unknown as { value: unknown }).value;
    setF({ ...latest.current, models: Array.isArray(v) ? v.map(String) : [] });
  });
  useForgeEvent(resetSel, "change", (e) => {
    const v = String((e.target as HTMLInputElement).value ?? "");
    setF({ ...latest.current, budgetDuration: (v || null) as ServiceKeyRequest["budgetDuration"] });
  });
  const create = useMutation({
    mutationFn: () => api.adminCreateServiceKey({ ...f, ...teamField }),
    onSuccess: async (k) => { await qc.invalidateQueries({ queryKey: ["service-keys"] }); onCreated(k); },
  });
  const save = useMutation({
    mutationFn: () => { const { name: _name, ...rest } = f; return api.adminUpdateServiceKey(target!.id, { ...rest, ...teamField }); },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["service-keys"] }); onClose(); },
  });
  const submit = target ? save : create;
  // The team is sent only while teams can be listed, so an unavailable list never changes a key's team.
  const teamsReady = teams.data?.available === true;
  const teamField = teamsReady ? { teamId } : {};
  const choices = (teams.data?.teams ?? []).filter((t) => t.assignable || t.id === target?.teamId);
  const twoPeople = f.owner && f.backupOwner && f.owner !== f.backupOwner;
  const ready = f.name.trim() && f.division && f.businessUnit && twoPeople;
  const legacy = target && missingOwners(target);

  return (
    <Dialog open onClose={onClose} label={target ? "Edit service key" : "Create a service key"} wide>
      <h2 className="forge-typography--heading3 card-title">{target ? `Edit “${shortAlias(target)}”` : "Create a service key"}</h2>
      <forge-text-field label-position="block-start">
        <label htmlFor="svc-name">Name</label>
        <input id="svc-name" value={f.name} maxLength={40} placeholder="e.g. erp-eco-agents" disabled={!!target || undefined}
          onChange={(e) => setF({ ...latest.current, name: e.target.value })} />
        <span slot="support-text">{target ? "The name cannot change; create a new key for a new name."
          : "The application or pipeline that will use it. Letters, digits and dashes."}</span>
      </forge-text-field>
      <div className="field-pair">
        <DivisionPicker taxonomy={me.taxonomy} division={f.division} businessUnit={f.businessUnit}
          onChange={(d, b) => setF({ ...latest.current, division: d, businessUnit: b })} />
      </div>
      <div className="field-pair">
        {/* Mounted once the users load, so Forge resolves the recorded owners' labels against the full list. */}
        {!users.data ? <Loading /> : <>
        <SearchPicker id="svc-owner" label="Owner" placeholder="Name or email" value={f.owner} filter={ownerFilter} block required
          supportText="Answers for the key, rotates it, and gets its expiry notices."
          onChange={(v) => setF({ ...latest.current, owner: v })} />
        <SearchPicker id="svc-backup-owner" label="Backup owner" placeholder="Name or email" value={f.backupOwner} filter={backupFilter}
          block required supportText="A different person, who takes over if the owner leaves."
          onChange={(v) => setF({ ...latest.current, backupOwner: v })} />
        </>}
      </div>
      {legacy && !twoPeople && (
        <forge-inline-message theme="warning">
          <forge-icon slot="icon" name="warning" />
          <div slot="title">Name {!f.owner && !f.backupOwner ? "an owner and a backup owner" : !f.owner ? "an owner" : "a backup owner"} to save</div>
          Every service key needs two people who answer for it. Rotating or revoking this key works without them.
        </forge-inline-message>
      )}
      <div className="field-pair">
        <forge-select label-position="block-start" ref={teamSel} label="Team" value={teamId} disabled={!teamsReady || undefined}>
          <forge-option value="">No team</forge-option>
          {choices.map((t) => (
            <forge-option key={t.id} value={t.id}>
              {t.alias ?? `Team …${t.id.slice(-8)}`} ({t.modelCount ? `${t.modelCount} ${t.modelCount === 1 ? "model" : "models"}` : "all models"}{t.assignable ? "" : "; outside this organization"})
            </forge-option>
          ))}
          <span slot="support-text">{teams.isPending ? "Loading teams…"
            : teamsReady ? "Only teams in the service organization. A team's own model list and limits can apply to the key."
            : "Teams cannot be listed yet, so the team cannot be changed here."}</span>
        </forge-select>
        <forge-select label-position="block-start" ref={modelSel} label="Models" multiple placeholder="All models" value={f.models}>
          {(models.data ?? []).map((m) => <forge-option key={m} value={m}>{m}</forge-option>)}
        </forge-select>
      </div>
      <div className="row gap-medium wrap" style={{ alignItems: "flex-start" }}>
        <forge-text-field label-position="block-start" style={{ flex: "1 1 160px" }}>
          <label htmlFor="svc-budget">Budget (USD)</label>
          <span slot="start">$</span>
          <input id="svc-budget" inputMode="decimal" value={budgetText} placeholder="No key limit"
            onChange={(e) => {
              setBudgetText(e.target.value);
              const n = e.target.value.trim() === "" ? null : Number(e.target.value);
              setF({ ...latest.current, maxBudget: n !== null && Number.isFinite(n) ? n : null, budgetDuration: n === null ? null : latest.current.budgetDuration });
            }} />
          <span slot="support-text">The key&apos;s own limit. No person&apos;s budget applies to a service key.</span>
        </forge-text-field>
        <forge-select label-position="block-start" ref={resetSel} label="Budget resets" value={f.budgetDuration ?? ""}
          disabled={f.maxBudget === null || undefined} style={{ flex: "1 1 160px" }}>
          <forge-option value="">Never</forge-option>
          <forge-option value="1d">Daily</forge-option>
          <forge-option value="7d">Weekly</forge-option>
          <forge-option value="30d">Monthly</forge-option>
        </forge-select>
      </div>
      {submit.error && <ErrorMessage error={submit.error} />}
      <div className="row gap-small">
        <forge-button variant="raised" disabled={!ready || submit.isPending || undefined} onClick={() => submit.mutate()}>{target ? "Save" : "Create"}</forge-button>
        <forge-button onClick={onClose}>Cancel</forge-button>
      </div>
    </Dialog>
  );
}
